import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  ClickAwayListener,
  Grow,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Paper,
  Popper,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import type { VisibilityState } from '@tanstack/react-table';
import type { ColumnMetadata } from '../../types';

type ColumnChooserPopoverProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  columns: ColumnMetadata[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
};

export const ColumnChooserPopover = ({
  anchorEl,
  open,
  onClose,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
}: ColumnChooserPopoverProps) => {
  const [search, setSearch] = useState('');

  const filteredColumns = useMemo(() => {
    const choosable = columns.filter((col) => col.field && col.visible !== false);
    if (!search) return choosable;
    const lower = search.toLowerCase();
    return choosable.filter((col) => (col.title || col.field).toLowerCase().includes(lower));
  }, [columns, search]);

  const handleToggle = useCallback(
    (field: string) => {
      const next = { ...columnVisibility, [field]: !(columnVisibility[field] ?? true) };
      onColumnVisibilityChange(next);
    },
    [columnVisibility, onColumnVisibilityChange]
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}>
          <Paper elevation={4}>
            <ClickAwayListener onClickAway={onClose}>
              <Box sx={{ width: 260, maxHeight: 340, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1 }}>
                  <TextField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search..."
                    size="small"
                    fullWidth
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <List dense sx={{ overflow: 'auto', flex: 1, pt: 0 }}>
                  {filteredColumns.map((col) => {
                    const isVisible = columnVisibility[col.field] ?? true;
                    return (
                      <ListItem key={col.field} disablePadding>
                        <ListItemButton onClick={() => handleToggle(col.field)} dense>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={isVisible}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={col.title || col.field} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};
